import React, { useState, useEffect } from 'react';
import { useToast } from '../hooks/use-toast';
import { Linkedin, Instagram, Share2, Copy, Check } from 'lucide-react';
import axios from 'axios';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export const Waitlist = () => {
  const [email, setEmail] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isJoined, setIsJoined] = useState(false);
  const [position, setPosition] = useState(null);
  const [referralLink, setReferralLink] = useState(null);
  const [referralCode, setReferralCode] = useState(null);
  const [copied, setCopied] = useState(false);
  const { toast } = useToast();

  // Check localStorage on mount
  useEffect(() => {
    const savedData = localStorage.getItem('saverr_waitlist');
    if (savedData) {
      try {
        const data = JSON.parse(savedData);
        setIsJoined(true);
        setPosition(data.position);
        setReferralLink(data.referralLink);
        setReferralCode(data.referralCode);
      } catch (error) {
        console.error('Error parsing saved waitlist data:', error);
      }
    }
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email) return;

    setIsSubmitting(true);
    
    try {
      const urlParams = new URLSearchParams(window.location.search);
      const refCode = urlParams.get('ref');

      const response = await axios.post(`${API}/waitlist/join`, {
        email,
        referral_code: refCode
      });

      if (response.data.success === false) {
        const existingData = response.data.existing_data;
        setPosition(existingData.position);
        setReferralLink(existingData.referral_link);
        setReferralCode(existingData.referral_code);
        setIsJoined(true);
        
        localStorage.setItem('saverr_waitlist', JSON.stringify({
          position: existingData.position,
          referralLink: existingData.referral_link,
          referralCode: existingData.referral_code
        }));

        toast({
          title: "Welcome back!",
          description: `You're already on the list at position #${existingData.position}`,
        });
      } else {
        setPosition(response.data.position);
        setReferralLink(response.data.referral_link);
        setReferralCode(response.data.referral_code);
        setIsJoined(true);
        
        localStorage.setItem('saverr_waitlist', JSON.stringify({
          position: response.data.position,
          referralLink: response.data.referral_link,
          referralCode: response.data.referral_code
        }));

        toast({
          title: "You're on the list!",
          description: `You're #${response.data.position} in line. Share your link to move up!`,
        });
      }

      setEmail('');
    } catch (error) {
      console.error('Error joining waitlist:', error);
      toast({
        title: "Error",
        description: "Failed to join waitlist. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const copyToClipboard = () => {
    navigator.clipboard.writeText(referralLink);
    setCopied(true);
    toast({
      title: "Link copied!",
      description: "Share it to move up the waitlist",
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const shareToSocial = (platform) => {
    const text = encodeURIComponent('Join me on Saverr - earn higher savings rates with automated optimization! 🚀');
    const url = encodeURIComponent(referralLink);
    
    const urls = {
      linkedin: `https://www.linkedin.com/sharing/share-offsite/?url=${url}`,
      instagram: `https://www.instagram.com/`,
      bluesky: `https://bsky.app/intent/compose?text=${text}%20${url}`
    };
    
    window.open(urls[platform], '_blank', 'width=600,height=600');
    
    toast({
      title: `Opening ${platform}...`,
      description: "Each share moves you up the waitlist",
    });
  };

  return (
    <section id="waitlist" className="py-24 bg-slate-900 relative overflow-hidden">
      <div className="absolute top-0 right-0 -mr-20 -mt-20 w-96 h-96 bg-blue-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>
      <div className="absolute bottom-0 left-0 -ml-20 -mb-20 w-96 h-96 bg-purple-600 rounded-full mix-blend-multiply filter blur-3xl opacity-20"></div>

      <div className="max-w-4xl mx-auto px-4 relative z-10 text-center">
        {!isJoined ? (
          <>
            <h2 className="text-3xl md:text-5xl font-bold text-white mb-6">Ready to maximize your savings?</h2>
            <p className="text-xl text-slate-300 mb-10 max-w-2xl mx-auto">
              Join thousands of smart savers who are earning more on their idle cash. Sign up for early access today.
            </p>
            
            <div className="bg-white/10 backdrop-blur-md p-2 rounded-2xl inline-block w-full max-w-lg border border-white/10">
              <form onSubmit={handleSubmit} className="flex flex-col sm:flex-row gap-2">
                <input
                  type="email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  placeholder="Enter your email address"
                  required
                  className="flex-1 bg-white/10 border border-white/10 text-white placeholder-slate-400 px-5 py-3 rounded-xl focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="px-8 py-3 bg-white text-slate-900 font-bold rounded-xl hover:bg-blue-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {isSubmitting ? 'Joining...' : 'Get Early Access'}
                </button>
              </form>
            </div>
            
            <p className="mt-6 text-sm text-slate-500">No credit card required. Unsubscribe at any time.</p>
          </>
        ) : (
          <div className="bg-white rounded-3xl shadow-2xl p-8 md:p-12">
            <div className="mb-10">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-green-100 rounded-full mb-6">
                <Check className="w-10 h-10 text-green-600" strokeWidth={3} />
              </div>
              <h2 className="text-4xl md:text-5xl font-bold text-slate-900 mb-4">You're In!</h2>
              <p className="text-xl text-slate-600 mb-2">
                You're <span className="font-bold text-blue-600">#{position}</span> on the waitlist
              </p>
              <p className="text-slate-500 font-medium">Move up faster by sharing your unique link</p>
            </div>

            <div className="bg-slate-50 rounded-2xl p-8 mb-8 border border-slate-200">
              <label className="block text-sm font-bold text-slate-700 mb-3">
                Your Referral Link
              </label>
              <div className="flex flex-col sm:flex-row gap-3">
                <input
                  type="text"
                  value={referralLink}
                  readOnly
                  className="flex-1 px-4 py-3 bg-white rounded-xl border border-slate-200 text-slate-700 text-sm font-mono"
                />
                <button
                  onClick={copyToClipboard}
                  className="flex items-center justify-center gap-2 bg-blue-600 text-white px-6 py-3 rounded-xl hover:bg-blue-700 transition-all font-semibold shadow-lg whitespace-nowrap"
                >
                  {copied ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                  {copied ? 'Copied!' : 'Copy Link'}
                </button>
              </div>
            </div>

            <div className="mb-8">
              <p className="text-sm font-bold text-slate-700 mb-4">
                Share on social media to move up faster
              </p>
              <div className="grid grid-cols-3 gap-4">
                <button
                  onClick={() => shareToSocial('linkedin')}
                  className="flex flex-col items-center gap-3 bg-blue-600 text-white p-6 rounded-2xl hover:bg-blue-700 transition-all hover:shadow-xl hover:scale-105"
                >
                  <Linkedin className="w-8 h-8" strokeWidth={2} />
                  <span className="font-bold text-sm">LinkedIn</span>
                </button>
                <button
                  onClick={() => shareToSocial('instagram')}
                  className="flex flex-col items-center gap-3 bg-gradient-to-br from-pink-500 to-rose-500 text-white p-6 rounded-2xl hover:from-pink-600 hover:to-rose-600 transition-all hover:shadow-xl hover:scale-105"
                >
                  <Instagram className="w-8 h-8" strokeWidth={2} />
                  <span className="font-bold text-sm">Instagram</span>
                </button>
                <button
                  onClick={() => shareToSocial('bluesky')}
                  className="flex flex-col items-center gap-3 bg-sky-600 text-white p-6 rounded-2xl hover:bg-sky-700 transition-all hover:shadow-xl hover:scale-105"
                >
                  <Share2 className="w-8 h-8" strokeWidth={2} />
                  <span className="font-bold text-sm">BlueSky</span>
                </button>
              </div>
            </div>

            <div className="p-6 bg-blue-50 rounded-2xl border border-blue-200">
              <p className="text-sm text-blue-900 font-semibold">
                <strong>Pro tip:</strong> Each successful referral moves you up 5 positions in the waitlist!
              </p>
            </div>
          </div>
        )}
      </div>
    </section>
  );
};
